import { Controller } from "@hotwired/stimulus"
import {
  fileExtensionIsAccepted,
  formatBytes,
  replaceInputFiles,
  selectedFiles
} from "../package_upload/files.js"
import {
  captureReviewValues,
  renderReviewRows,
  renumberReviewRows,
  reviewField,
  reviewPeriodSignature,
  reviewRows,
  syncReviewPeriodFields
} from "../package_upload/review_rows.js"
import {
  clearClientError,
  fileSelectionError,
  refreshValidationSummary,
  validateUploadForm
} from "../package_upload/validation.js"

export default class extends Controller {
  static targets = [
    "filesInput",
    "folderInput",
    "dropzone",
    "fileError",
    "pickerTitle",
    "details",
    "detailsHeading",
    "fileRows",
    "rowTemplate",
    "fileCount",
    "totalSize",
    "bulkCategory",
    "bulkPeriodKind",
    "bulkPeriodYear",
    "bulkPeriodMonth",
    "bulkStatus",
    "bulkYearField",
    "bulkMonthField",
    "packageTitle",
    "packagePeriodKind",
    "packagePeriodYear",
    "packagePeriodMonth",
    "packageYearField",
    "packageMonthField",
    "defaultsStatus",
    "statusBadge",
    "visibilityNote",
    "validationSummary"
  ]

  static values = {
    detailsVisible: Boolean,
    defaultTitle: String,
    packageTitleEdited: Boolean,
    packagePeriodEdited: Boolean,
    existingPackage: Boolean,
    maxFileSize: Number,
    maxTotalSize: Number
  }

  connect() {
    this.dragDepth = 0
    this.activeInput = null
    this.packageTitleWasEdited = this.packageTitleEditedValue
    this.packagePeriodWasEdited = this.packagePeriodEditedValue
    this.syncBulkPeriodFields()
    this.syncPackagePeriodFields()
    this.syncPublicationSummary()
    this.rows.forEach(syncReviewPeriodFields)
    this.detailsTarget.hidden = !this.detailsVisibleValue && this.rows.length === 0
  }

  filesChanged() {
    this.selectFrom(this.filesInputTarget, this.folderInputTarget)
  }

  folderChanged() {
    this.selectFrom(this.folderInputTarget, this.filesInputTarget)
  }

  removeFile(event) {
    const row = event.currentTarget.closest("[data-package-upload-target='row']")
    if (!row) return

    if (this.activeInput) {
      const removedIndex = Number(row.dataset.rowIndex)
      const remainingFiles = Array.from(this.activeInput.files).filter((_file, index) => index !== removedIndex)
      row.remove()
      replaceInputFiles(this.activeInput, remainingFiles)
      this.renderFiles(remainingFiles, { moveFocus: false })
      return
    }

    row.remove()
    renumberReviewRows(this.rows)
    this.clearDefaultsStatus()
    this.updateSelectionSummary([])
    if (this.rows.length === 0) this.detailsTarget.hidden = true
    const remainingFileError = this.rows.some(reviewRow => (
      reviewRow.classList.contains("has-file-error")
    ))
    const fileMessage = remainingFileError
      ? "Fix or remove the files marked below, then choose the remaining files again."
      : fileSelectionError({
          files: [],
          accept: this.filesInputTarget.accept,
          reviewedFileCount: this.rows.length,
          maxFileSize: this.maxFileSizeValue,
          maxTotalSize: this.maxTotalSizeValue
        })
    this.syncFileError(fileMessage)
    refreshValidationSummary(this.element, this.validationSummaryTarget)
  }

  dragEntered(event) {
    event.preventDefault()
    this.dragDepth += 1
    this.dropzoneTarget.classList.add("dragging")
  }

  dragOver(event) {
    event.preventDefault()
  }

  dragLeft(event) {
    event.preventDefault()
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.dropzoneTarget.classList.remove("dragging")
  }

  filesDropped(event) {
    event.preventDefault()
    this.dragDepth = 0
    this.dropzoneTarget.classList.remove("dragging")

    const files = Array.from(event.dataTransfer.files)
    this.folderInputTarget.value = ""
    this.activeInput = this.filesInputTarget
    replaceInputFiles(this.filesInputTarget, files)
    this.clearFileError()
    this.renderFiles(files, { moveFocus: true })
  }

  bulkPeriodChanged() {
    this.syncBulkPeriodFields()
    this.clearDefaultsStatus()
  }

  defaultsChanged() {
    this.clearDefaultsStatus()
  }

  applyDefaults() {
    this.rows.forEach(row => {
      const category = reviewField(row, "category")
      const periodKind = reviewField(row, "periodKind")
      const periodYear = reviewField(row, "periodYear")
      const periodMonth = reviewField(row, "periodMonth")
      const status = reviewField(row, "status")

      category.value = this.bulkCategoryTarget.value
      periodKind.value = this.bulkPeriodKindTarget.value
      periodYear.value = this.bulkPeriodYearTarget.value
      periodMonth.value = this.bulkPeriodMonthTarget.value
      if (status && this.hasBulkStatusTarget) status.value = this.bulkStatusTarget.value
      syncReviewPeriodFields(row)
      const fields = [ category, periodKind, periodYear, periodMonth, status ].filter(Boolean)
      fields.forEach(clearClientError)
    })
    this.syncSuggestedPackagePeriod()
    refreshValidationSummary(this.element, this.validationSummaryTarget)
    const count = this.rows.length
    this.defaultsStatusTarget.textContent = `Applied to ${count} ${count === 1 ? "file" : "files"}.`
  }

  rowChanged(event) {
    const row = event.currentTarget.closest("[data-package-upload-target='row']")
    if (!row) return

    syncReviewPeriodFields(row)
    this.syncSuggestedPackagePeriod()
  }

  packageTitleChanged() {
    this.packageTitleWasEdited = true
  }

  packagePeriodChanged() {
    this.packagePeriodWasEdited = true
    this.syncPackagePeriodFields()
    this.updateSuggestedPackageTitle()
  }

  publicationChanged() {
    this.syncPublicationSummary()
  }

  fieldChanged(event) {
    clearClientError(event.target)
    refreshValidationSummary(this.element, this.validationSummaryTarget)
  }

  validateSubmission(event) {
    const { invalidFields, fileMessage } = validateUploadForm({
      form: this.element,
      summary: this.validationSummaryTarget,
      files: this.selectedFiles,
      filesInput: this.filesInputTarget,
      folderInput: this.folderInputTarget,
      activeInput: this.activeInput,
      reviewedFileCount: this.rows.length,
      maxFileSize: this.maxFileSizeValue,
      maxTotalSize: this.maxTotalSizeValue
    })
    this.syncFileError(fileMessage)
    if (invalidFields.length === 0) return

    event.preventDefault()
    const invalidFileRow = this.rows.find(row => row.classList.contains("has-file-error"))
    const focusTarget = invalidFileRow?.querySelector(".upload-remove-file") || invalidFields[0]
    focusTarget.focus()
  }

  selectFrom(input, otherInput) {
    otherInput.value = ""
    this.activeInput = input
    this.clearFileError()
    this.renderFiles(Array.from(input.files), { moveFocus: true })
  }

  renderFiles(files, { moveFocus }) {
    this.resetFileInputValidation()
    this.clearDefaultsStatus()
    const savedRows = captureReviewValues(this.rows)
    renderReviewRows({
      container: this.fileRowsTarget,
      template: this.rowTemplateTarget,
      files,
      savedRows,
      defaults: {
        category: this.bulkCategoryTarget.value,
        periodKind: this.bulkPeriodKindTarget.value,
        periodYear: this.bulkPeriodYearTarget.value,
        periodMonth: this.bulkPeriodMonthTarget.value,
        status: this.hasBulkStatusTarget ? this.bulkStatusTarget.value : undefined
      }
    })

    this.detailsTarget.hidden = files.length === 0
    this.dropzoneTarget.classList.toggle("has-files", files.length > 0)
    this.updateSelectionSummary(files)
    this.markFileErrors(files)
    this.syncFileError(fileSelectionError({
      files,
      accept: this.filesInputTarget.accept,
      reviewedFileCount: this.rows.length,
      maxFileSize: this.maxFileSizeValue,
      maxTotalSize: this.maxTotalSizeValue
    }))
    this.validationSummaryTarget.hidden = true
    this.syncSuggestedPackagePeriod()
    refreshValidationSummary(this.element, this.validationSummaryTarget)

    if (moveFocus && files.length > 0) {
      window.requestAnimationFrame(() => this.detailsHeadingTarget.focus())
    }
  }

  clearFileError() {
    this.resetFileInputValidation()
    this.syncFileError(null)
    this.validationSummaryTarget.hidden = true
  }

  resetFileInputValidation() {
    this.filesInputTarget.setCustomValidity("")
    this.folderInputTarget.setCustomValidity("")
    const inputs = [ this.filesInputTarget, this.folderInputTarget ]
    inputs.forEach(input => {
      input.classList.remove("error")
      input.removeAttribute("data-client-invalid")
      input.setAttribute("aria-invalid", "false")
    })
  }

  syncFileError(message) {
    const hasError = Boolean(message)
    this.dropzoneTarget.classList.toggle("error", hasError)
    this.fileErrorTarget.textContent = message || ""
    this.fileErrorTarget.hidden = !hasError
    this.filesInputTarget.setAttribute("aria-invalid", String(hasError))
    this.folderInputTarget.setAttribute("aria-invalid", String(hasError))
  }

  markFileErrors(files) {
    this.rows.forEach((row, index) => {
      const file = files[index]
      let message = null

      if (!fileExtensionIsAccepted(file.name, this.filesInputTarget.accept)) {
        message = "This file type isn't supported. Remove it or choose a supported document."
      } else if (file.size === 0) {
        message = "This file is empty. Remove it or choose another document."
      } else if (file.size > this.maxFileSizeValue) {
        message = `This file is larger than ${formatBytes(this.maxFileSizeValue)}. Remove it or choose a smaller document.`
      }

      const error = row.querySelector("[data-upload-display='fileError']")
      row.classList.toggle("has-file-error", Boolean(message))
      error.textContent = message || ""
      error.hidden = !message
    })
  }

  clearDefaultsStatus() {
    this.defaultsStatusTarget.textContent = ""
  }

  syncBulkPeriodFields() {
    const kind = this.bulkPeriodKindTarget.value
    this.bulkYearFieldTarget.hidden = kind === ""
    this.bulkMonthFieldTarget.hidden = kind !== "monthly"
  }

  syncSuggestedPackagePeriod() {
    if (this.existingPackageValue || this.packagePeriodWasEdited) return

    const signatures = this.rows.map(reviewPeriodSignature)
    const first = signatures[0] || { kind: "", year: "", month: "" }
    const allMatch = signatures.every(signature => (
      signature.kind === first.kind &&
      signature.year === first.year &&
      signature.month === first.month
    ))
    const complete = first.kind === "" || (
      first.year !== "" && (first.kind !== "monthly" || first.month !== "")
    )
    const suggested = allMatch && complete ? first : { kind: "", year: "", month: "" }

    this.packagePeriodKindTarget.value = suggested.kind
    this.packagePeriodYearTarget.value = suggested.year || this.bulkPeriodYearTarget.value
    this.packagePeriodMonthTarget.value = suggested.month || this.bulkPeriodMonthTarget.value
    this.syncPackagePeriodFields()
    this.updateSuggestedPackageTitle()
  }

  syncPackagePeriodFields() {
    if (!this.hasPackagePeriodKindTarget) return

    const kind = this.packagePeriodKindTarget.value
    this.packageYearFieldTarget.hidden = kind === ""
    this.packageMonthFieldTarget.hidden = kind !== "monthly"
    this.packagePeriodYearTarget.required = kind !== ""
    this.packagePeriodMonthTarget.required = kind === "monthly"
  }

  syncPublicationSummary() {
    if (!this.hasStatusBadgeTarget || !this.hasVisibilityNoteTarget) return

    const status = this.element.querySelector("input[name='draft_package_upload[package_status]']:checked")?.value || "draft"
    const published = status === "published"

    this.statusBadgeTarget.textContent = published ? "Published" : "Draft"
    this.statusBadgeTarget.classList.toggle("status-published", published)
    this.statusBadgeTarget.classList.toggle("status-review", !published)
    this.visibilityNoteTarget.textContent = published
      ? "Visible to linked company users. No email is sent."
      : "Hidden from company users."
  }

  updateSuggestedPackageTitle() {
    if (this.existingPackageValue || this.packageTitleWasEdited || !this.hasPackageTitleTarget) return

    const kind = this.packagePeriodKindTarget.value
    const year = this.packagePeriodYearTarget.value

    if (kind === "yearly" && year !== "") {
      this.packageTitleTarget.value = `${year} documents`
      return
    }

    if (kind === "monthly" && year !== "") {
      const month = this.packagePeriodMonthTarget.options[this.packagePeriodMonthTarget.selectedIndex]?.text
      if (month) {
        this.packageTitleTarget.value = `${month} ${year} documents`
        return
      }
    }

    this.packageTitleTarget.value = this.defaultTitleValue
  }

  updateSelectionSummary(files) {
    const count = files.length || this.rows.length
    this.fileCountTarget.textContent = `${count} ${count === 1 ? "file" : "files"}`
    this.pickerTitleTarget.textContent = files.length > 0
      ? `${count} ${count === 1 ? "file" : "files"} selected`
      : "Drop documents here"
    this.totalSizeTarget.textContent = files.length > 0
      ? formatBytes(files.reduce((sum, file) => sum + file.size, 0))
      : "Choose the files again"
  }

  get rows() {
    return reviewRows(this.fileRowsTarget)
  }

  get selectedFiles() {
    return selectedFiles(this.activeInput, this.filesInputTarget, this.folderInputTarget)
  }
}
